import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";

import { useTopicDetailsQuery, useTopicGroupDetailsQuery } from "api/hooks";
import { minutesToTime } from "utils";
import { ScheduleModel } from "../MonthCalendar";

const POPOVER_WIDTH = 340;
const VIEWPORT_GUTTER = 8;
const MAX_GROUP_TOPICS = 8;

interface ScheduleItemPreviewPopoverProps {
  item: ScheduleModel;
  // Captured at click time - table cells remount on every render, so the anchor element itself can't be tracked.
  anchorRect: DOMRect;
  onClose: () => void;
  onOpenEdit?: (item: ScheduleModel) => void;
}

export const ScheduleItemPreviewPopover = ({
  item,
  anchorRect,
  onClose,
  onOpenEdit,
}: ScheduleItemPreviewPopoverProps) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [placeAbove, setPlaceAbove] = useState(false);
  const isGroup = !!item.topicGroupId;

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // The position is fixed to the click-time rect, so any scroll/resize would detach it from the row.
    const onViewportChange = () => onClose();

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
    };
  }, [onClose]);

  // Flip above the anchor when there's not enough room below.
  useLayoutEffect(() => {
    const height = popoverRef.current?.offsetHeight ?? 0;
    const roomBelow = window.innerHeight - anchorRect.bottom;
    setPlaceAbove(roomBelow < height + VIEWPORT_GUTTER * 2 && anchorRect.top > roomBelow);
  });

  const left = Math.max(
    VIEWPORT_GUTTER,
    Math.min(anchorRect.left - 16, window.innerWidth - POPOVER_WIDTH - VIEWPORT_GUTTER),
  );
  const style: React.CSSProperties = placeAbove
    ? { left, bottom: window.innerHeight - anchorRect.top + VIEWPORT_GUTTER, width: POPOVER_WIDTH }
    : { left, top: anchorRect.bottom + VIEWPORT_GUTTER, width: POPOVER_WIDTH };

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={isGroup ? "Podgląd grupy tematów" : "Podgląd tematu"}
      style={style}
      className="fixed z-50 rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800"
    >
      <div className="relative px-4 pb-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          title="Zamknij"
        >
          <IoClose size={18} />
        </button>
        <ScheduleItemBadge item={item} />
        {isGroup ? <TopicGroupPreview topicGroupId={item.topicGroupId!} /> : <TopicPreview topicId={item.topicId!} />}
      </div>
      {onOpenEdit && (
        <div className="flex justify-end border-t border-slate-100 px-4 py-2.5 dark:border-slate-700">
          <button
            type="button"
            onClick={() => {
              onClose();
              onOpenEdit(item);
            }}
            className="h-8 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-primary-500 shadow-sm hover:border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:hover:border-slate-500"
          >
            Otwórz pełną edycję
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
};

const TopicPreview = ({ topicId }: { topicId: number }) => {
  const { data: topic, isLoading, isError } = useTopicDetailsQuery(topicId);

  if (isLoading) return <PreviewPlaceholder text="Ładowanie..." />;
  if (isError || !topic) return <PreviewPlaceholder text="Nie udało się pobrać tematu." />;

  const description = htmlToText(topic.description);
  return (
    <>
      <div className="mt-2 pr-6 text-[15px] font-semibold text-slate-900 dark:text-slate-100">{topic.title}</div>
      {description ? (
        <p className="mt-1.5 line-clamp-5 text-[13px] leading-normal text-slate-500 dark:text-slate-400">
          {description}
        </p>
      ) : (
        <p className="mt-1.5 text-[13px] italic text-slate-400">Brak opisu.</p>
      )}
      <DurationLine minutes={topic.time} />
    </>
  );
};

const TopicGroupPreview = ({ topicGroupId }: { topicGroupId: number }) => {
  const { data: group, isLoading, isError } = useTopicGroupDetailsQuery(topicGroupId);

  if (isLoading) return <PreviewPlaceholder text="Ładowanie..." />;
  if (isError || !group) return <PreviewPlaceholder text="Nie udało się pobrać grupy tematów." />;

  const visibleTopics = group.topics.slice(0, MAX_GROUP_TOPICS);
  const hiddenCount = group.topics.length - visibleTopics.length;
  return (
    <>
      <div className="mt-2 pr-6 text-[15px] font-semibold text-slate-900 dark:text-slate-100">{group.name}</div>
      {group.topics.length > 0 ? (
        <ul className="mt-2 space-y-1 text-[13px] text-slate-600 dark:text-slate-300">
          {visibleTopics.map((topic) => (
            <li key={topic.id} className="flex justify-between gap-3">
              <span className="truncate">&bull;&nbsp;&nbsp;{topic.title}</span>
              {topic.time > 0 && <span className="shrink-0 text-slate-400">{minutesToTime(topic.time)}</span>}
            </li>
          ))}
          {hiddenCount > 0 && <li className="text-slate-400">i {hiddenCount} więcej...</li>}
        </ul>
      ) : (
        <p className="mt-1.5 text-[13px] italic text-slate-400">Grupa nie zawiera tematów.</p>
      )}
      <DurationLine minutes={group.time} />
    </>
  );
};

const DurationLine = ({ minutes }: { minutes: number }) =>
  minutes > 0 ? (
    <div className="mt-3 text-xs text-slate-400">
      Czas trwania: <span className="font-medium text-slate-700 dark:text-slate-200">{minutesToTime(minutes)}</span>
    </div>
  ) : null;

const PreviewPlaceholder = ({ text }: { text: string }) => (
  <div className="mt-3 text-[13px] text-slate-400">{text}</div>
);

export const ScheduleItemBadge = ({ item }: { item: ScheduleModel }) => {
  if (item.topicGroupId)
    return <Badge className="bg-primary-50 text-primary-800 dark:bg-primary-800 dark:text-primary-50">Grupa</Badge>;
  if (item.topicId)
    return <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">Temat</Badge>;
  if (item.examId)
    return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-200">Egzamin</Badge>;
  return (
    <Badge className="border border-dashed border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-600 dark:bg-transparent dark:text-slate-500">
      Przerwa
    </Badge>
  );
};

const Badge = ({ className, children }: { className: string; children: React.ReactNode }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}
  >
    {children}
  </span>
);

// Topic descriptions are Quill HTML - the preview shows them as plain text (DOMParser doesn't execute scripts).
function htmlToText(html?: string): string {
  if (!html) return "";
  const text = new DOMParser().parseFromString(html, "text/html").body.textContent ?? "";
  return text.replace(/\s+/g, " ").trim();
}
